import React, { useMemo, useState } from "react";
import styled from "styled-components";
import facebook from "../../../assets/images/facebook-logo.svg";
import nineacres from "../../../assets/images/integration/99acres.png";
import excel from "../../../assets/images/integration/excel.png";
import googleforms from "../../../assets/images/integration/googleforms.png";
import housing from "../../../assets/images/integration/housing.jpg";
import indiamart from "../../../assets/images/integration/indiamart.png";
import justdial from "../../../assets/images/integration/justdial.png";
import leadform from "../../../assets/images/integration/leadform.png";
import magicbricks from "../../../assets/images/integration/magicbricks.png";
import tradeindia from "../../../assets/images/integration/tradeIndia.png";
import typeform from "../../../assets/images/integration/typeform.png";
import word from "../../../assets/images/integration/word.png";
import wordpress from "../../../assets/images/integration/wordpress.png";
import zapiuer from "../../../assets/images/integration/zapiuer.svg";
import customform from "../../../assets/images/integration/Custom Form.jpeg";
import LeadLabel from "../../../components/leadLabel";
import LeadStatus from "../../../components/leadStatus";
import ContactLinksGenerator from "../../../utils/contactlinksgenerator";
import whatsappbusiness from "../../../assets/images/whatsappbusiness.png";

import moment from "moment";
import "../leads.css";
import { addCountryCode } from "../../../utils/helpers";
import { FaEnvelope, FaPhone, FaRegClock, FaWhatsapp } from "react-icons/fa";
import { Phone, Mail, Clock, UserPlus, FileText } from "lucide-react"; // Corrected WhatsApp import
import UserCard from "../../../components/UserCard";
import logger from "../../../utils/logger";

const StyledImg = styled.img`
  margin-right: 5px;
  width: 20px;        // ✅ INCREASED from 12px
  height: 20px;       // ✅ INCREASED from 12px
  object-fit: contain; // ✅ PRESERVES aspect ratio
`;
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* Consistent space between icons */
`;

const IconHolder = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.disabled
      ? "#E0E0E0"
      : "#f1f3f5"}; /* Light background for enabled, gray for disabled */
  box-shadow: ${(props) =>
    props.disabled
      ? "none"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"}; /* Shadow for enabled icons */
  cursor: ${(props) =>
    props.disabled ? "default" : "pointer"}; /* Pointer cursor if enabled */
`;

const nameRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }
  return (
    <div className="h-100 align-items-center d-flex" style={{ width: '100%' }}>
      <div className="lead_cell" style={{ width: '100%' }}>
        <div
          className="cell_label"
          title={value}
          style={{
            fontWeight: 700,
            fontSize: '15px',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};




// Type-safe phone renderer diagnostic wrapper
const phoneRendererTest = React.memo(({ value, data, node }: any) => {
  // First create the original renderer output
  const rendererOutput = phoneRenderer({ value, data, node });
  
  // Safe wrapper that checks if the output exists
  if (!rendererOutput) {
    return rendererOutput; // Return null if the original returned null
  }
  
  // Wrap with diagnostic code
  return React.cloneElement(rendererOutput, {
    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
      
      
      // Stop propagation at the outmost wrapper level
      e.stopPropagation();
      e.preventDefault();
      
      // Add this for old IE support and capturing-phase listeners
      if (e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
      
      // Still call the original onClick if it exists
      if (rendererOutput.props.onClick) {
        rendererOutput.props.onClick(e);
      }
    }
  });
}, (prev, next) => 
  prev.data?.phone === next.data?.phone && 
  prev.data?.email === next.data?.email
);

const phoneRenderer = React.memo(({ value, data, node }: any) => {
  // Handle action button clicks
  const handleActionClick = (e: React.MouseEvent<HTMLDivElement>, type: 'tel' | 'mail' | 'whatsapp') => {
    e.stopPropagation();  // Stop event bubbling
    e.preventDefault();    // Prevent default behavior
    
    // Only create ContactLinksGenerator when needed
    const contactObj = new ContactLinksGenerator({
      number: data?.phone,
      email: data?.email,
      message: "Hello"
    });
    
    // Get the appropriate link based on type
    let href: string | undefined;
    switch (type) {
      case 'tel':
        href = contactObj.tel;
        break;
      case 'mail':
        href = contactObj.mail;
        break;
      case 'whatsapp':
        href = contactObj.whatsapp;
        break;
    }
    
    // If we have a valid href, open it
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <IconWrapper onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
      {[
        {
          type: 'tel' as const,
          disabled: !data?.phone,
          icon: <Phone size={14} color={data?.phone ? "#007BFF" : "#A9A9A9"} />,
          ariaLabel: "Call contact"
        },
        {
          type: 'mail' as const,
          disabled: !data?.email,
          icon: <Mail size={14} color={data?.email ? "#0056D2" : "#A9A9A9"} />,
          ariaLabel: "Email contact"
        },
        {
          type: 'whatsapp' as const,
          disabled: !data?.phone,
          icon: <FaWhatsapp size={14} color={data?.phone ? "#25D366" : "#A9A9A9"} />,
          ariaLabel: "WhatsApp contact"
        }
      ].map(({ type, disabled, icon, ariaLabel }, index) => (
        <IconHolder 
          key={index} 
          disabled={disabled}
          onClick={(e: React.MouseEvent<HTMLDivElement>) => 
            !disabled && handleActionClick(e, type)
          }
          role="button"
          aria-label={ariaLabel}
          aria-disabled={disabled}
        >
          {icon}
        </IconHolder>
      ))}
    </IconWrapper>
  );
}, (prev, next) => 
  prev.data?.phone === next.data?.phone && 
  prev.data?.email === next.data?.email
);

// Export the new optimized renderer
export const optimizedPhoneRenderer = phoneRenderer;

// Fixed statusRenderer with proper TypeScript types
interface StatusRendererProps {
  value: string[] | undefined;
  preferences: any;
  data: any;
  onStatusClick?: (data: any) => void;
}

const statusRenderer: React.FC<any> = (props) => {
  // Add default empty preferences if not provided
  const { value, preferences = { status: [] }, data, onStatusClick } = props;
  
  // Only check if value is missing, not preferences
  if (!value) {
    return <span>-</span>;
  }
  
  const handleStatusClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent row selection
    
    // If onStatusClick function exists, call it with the data
    if (typeof onStatusClick === 'function') {
      onStatusClick(data);
    }
  };

  return (
    <div 
      className="d-flex align-items-center h-100 cursor-pointer"
      onClick={handleStatusClick}
      role="button"
      aria-label="Edit status"
      style={{
        height: '100%',
        width: '100%',
        padding: '4px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        textAlign: 'center'
      }}
      onMouseEnter={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = '#f5f5f5';
        target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = 'transparent';
        target.style.boxShadow = 'none';
      }}
    >
      <div className="lead-subtext" style={{ width: '100%' }}>
        <LeadStatus preferences={preferences} status={value} isTag={true} />
      </div>
    </div>
  );
};

// Updated LabelRenderer component that handles clicks
const LabelRenderer: React.FC<any> = (props) => {
  // Add default empty preferences if not provided
  const { value, preferences = { labels: [] }, data } = props;
  
  // Create tooltip content with all labels - MOVED BEFORE EARLY RETURN
  const tooltipContent = useMemo(() => {
    if (!value || !Array.isArray(value)) return '';
    
    const leadLabelsData = JSON.parse(
      localStorage.getItem("user_preferences") || "{}"
    )?.labels?.filter((item: { value: any }) => value?.includes(item.value)) || [];
    
    return leadLabelsData.length > 2 
      ? `All labels: ${leadLabelsData.map((label: any) => label.name).join(', ')}`
      : '';
  }, [value]);
  
  // Only check if value is missing, not preferences
  if (!value) {
    return <span>-</span>;
  }
  
  // Simply render the labels without any click handler
  return (
    <div 
      className="d-flex align-items-center h-100" 
      title={tooltipContent}
      style={{ width: '100%' }}
    >
      <div className="lead-subtext" style={{ width: '100%' }}>
        <LeadLabel preferences={preferences} label={value} isTag={true} />
      </div>
    </div>
  );
};

// Export with lowercase name for compatibility
const labelRenderer = LabelRenderer;

// Renderer for Facebook campaign name
const campaignNameRenderer: React.FC<any> = (props) => {
  const { data } = props;
  // Safely access nested property with fallbacks
  const campaignName = data?.extraDetails?.campaign_name;
  
  if (!campaignName) {
    return <span>-</span>;
  }
  
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={campaignName}>
          {campaignName}
        </div>
      </div>
    </div>
  );
};

// Renderer for Facebook form name
const formNameRenderer: React.FC<any> = (props) => {
  const { data } = props;
  // Safely access nested property with fallbacks
  const formName = data?.extraDetails?.formName;
  
  if (!formName) {
    return <span>-</span>;
  }
  
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={formName}>
          {formName}
        </div>
      </div>
    </div>
  );
};

const createdByRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={value}>
          {value}
        </div>
      </div>
    </div>
  );
};


const sourceRenderer: React.FC<any> = (props) => {
  const { value, data } = props;
  if (!value || !data) {
    return <span>-</span>;
  }

  const { customSource } = data;
  const imageMap: { [key: string]: string | JSX.Element } = {
    "99Acres": nineacres,
    CSV: excel,
    "Google spread sheets": googleforms,
    Housing: housing,
    Indiamart: indiamart,
    "Just Dial": justdial,
    "Magic Bricks": magicbricks,
    Tradeindia: tradeindia,
    "Type Form": typeform,
    Word: word,
    Wordpress: wordpress,
    Zapier: zapiuer,
    Facebook: facebook,
    "Lead Form": <FileText size={20} color="#4F46E5" />, 
    "Custom Form": customform,
    "Whatsapp Business": whatsappbusiness,
  };

  const source =
    typeof value === "string" ? value : value?.name || customSource;
  const icon = imageMap[source];

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label d-flex align-items-center" title={source}>
          {typeof icon === "string" ? (
            <StyledImg src={icon} /> // ✅ REMOVED height="12" width="12" attributes
          ) : (
            icon
          )}
          <span>{source}</span>
        </div>
      </div>
    </div>
  );
};

const lastActivityRenderer: React.FC<any> = (props) => {
  const { value, data } = props;
  if (!value || !data) {
    return <span>-</span>;
  }

  // Simply display the lastActivityLabel - nothing else
  return (
    <div
      className="h-100"
      style={{
        display: "flex",
        alignItems: "center",
        fontSize: "0.9rem",
      }}
    >
      {value} {/* value is lastActivityLabel */}
    </div>
  );
};

const assignedToRenderer = React.memo(({ value, data, context }: any) => {
  // Handle click on the cell
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    
    // Call the handler from context if available
    if (context && typeof context.onAssignedToClick === 'function' && data) {
      context.onAssignedToClick(data);
    }
  };
  
  // Simple styling for cell content
  const cellStyle = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };
  
  // Use UserCard for consistent display
  return (
    <div
      className="h-100 d-flex align-items-center"
      onClick={handleClick}
      style={cellStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f5f5f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <UserCard 
        user={value || null}
        size="sm"
        className="d-flex w-100"
      />
      
     
    </div>
  );
});



const locationRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={value}>
          {value.address}
        </div>
      </div>
    </div>
  );
};

const dateTimeRenderer = React.memo(({ value }: { value: string }) => {
  const relativeTime = useMemo(() => 
    value ? moment(value).fromNow() : "-"
  , [value]);

  const tooltipContent = useMemo(() => 
    value ? `${moment(value).format("MMMM D, YYYY, hh:mm A")} (${moment(value).fromNow()})` : ""
  , [value]);

  return (
    <div className="h-100 align-items-center d-flex justify-content-center" style={{ width: '100%' }}>
      <span 
        className="text-sm" 
        style={{ 
          fontSize: '0.9rem', 
          textAlign: 'center',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        title={tooltipContent}
      >
        {relativeTime}
      </span>
    </div>
  );
}, (prev, next) => prev.value === next.value);



const dateRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }
  const formattedDate = moment(value).format("DD MMM YYYY"); // e.g., 12 Nov 2024
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={formattedDate}>
          {formattedDate}
        </div>
      </div>
    </div>
  );
};

const amountRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }
  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label">{`₹ ${value.toLocaleString("en-IN")}`}</div>
      </div>
    </div>
  );
};


const saleValueRenderer = (params: any) => {
  const { data, onValueClick } = params;
  
  const value = parseFloat(data?.saleValue || "0");
  const productsCount = data?.products?.length || 0;
  
  const formatValue = (val: number): string => {
    if (val >= 10000000) { // 1 crore = 10,000,000
      return (val / 10000000).toFixed(2) + " Cr";
    } else if (val >= 100000) { // 1 lakh = 100,000
      return (val / 100000).toFixed(2) + " L";
    } else {
      return val.toLocaleString("en-IN");
    }
  };
  
  const handleClick = (e: React.MouseEvent) => {
    logger.log("SALE VALUE CELL CLICKED");
    e.stopPropagation(); // Prevent row selection
    e.preventDefault(); // Also prevent default behavior
    
    if (typeof onValueClick === 'function' && data) {
      onValueClick(data);
    }
  };
  
  return (
    <div 
      className="sale-value-cell" 
      onClick={handleClick}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        boxSizing: 'border-box'
      }}
      onMouseEnter={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = '#e8f4fd';
        target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        const target = e.currentTarget;
        target.style.backgroundColor = 'transparent';
        target.style.boxShadow = 'none';
      }}
    >
      <div style={{ pointerEvents: 'none', width: '100%' }}>
        <div style={{ 
          color: "#0056b3", 
          fontWeight: '500',
          lineHeight: '1.2'
        }}>
          ₹ {formatValue(value)}
        </div>
        {productsCount > 0 && (
          <div style={{ 
            fontSize: '0.8rem',
            color: '#6c757d',
            marginTop: '2px',
            lineHeight: '1.2'
          }}>
            {productsCount} {productsCount === 1 ? 'product' : 'products'}
          </div>
        )}
      </div>
    </div>
  );
};

// URL Field Component  
const UrlFieldRenderer: React.FC<{ value: any; field: any; data: any; context: any }> = ({ value, field, data, context }) => {
  const formatUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleUrlClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(formatUrl(value), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div 
          className="cell_label" 
          style={{
            color: '#1890ff',
            textDecoration: 'underline',
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
          title={`Click to open: ${value}`}
          onClick={handleUrlClick}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

// Address Field Component - No inline editing (address editing is tricky)
const AddressFieldRenderer: React.FC<{ value: any; field: any; data: any; context: any }> = ({ value, field, data, context }) => {
  const displayAddress = typeof value === 'object' ? value.address || value.location : value;
  
  const handleAddressClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const encodedAddress = encodeURIComponent(displayAddress);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div 
          className="cell_label" 
          style={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
          title={`Click to open in maps: ${displayAddress}`}
          onClick={handleAddressClick}
        >
          📍 {displayAddress}
        </div>
      </div>
    </div>
  );
};

// Editable custom field renderer
const editableCustomFieldRenderer: React.FC<any> = (params) => {
  const { value, data, context, colDef } = params;
  
  // Add safety check for data
  if (!data) {
    console.warn('EditableCustomFieldRenderer: No data provided', params);
    return <span>-</span>;
  }
  
  // Get the field configuration from context or colDef
  const field = context?.customFields?.find((f: any) => f.value === colDef.colId) || {
    value: colDef.colId,
    label: colDef.headerName,
    type: 'text',
    options: []
  };

  // For URL fields, use specialized rendering component
  if (field.type === 'url' && value && !field.read_only) {
    return <UrlFieldRenderer value={value} field={field} data={data} context={context} />;
  }

  // For address fields, use specialized rendering component
  if (field.type === 'address' && value && !field.read_only) {
    return <AddressFieldRenderer value={value} field={field} data={data} context={context} />;
  }

  // Don't make read-only fields editable
  if (field.read_only) {
    return (
      <div className="h-100 align-items-center d-flex">
        <div className="lead_cell">
          <div className="cell_label" title={value || '-'}>
            {value || '-'}
          </div>
        </div>
      </div>
    );
  }

  // Use simple display for other field types (editing handled by overlay)
  const displayValue = () => {
    if (!value) return '-';
    
    // Handle location objects
    if (typeof value === 'object' && value.address) {
      return value.address;
    }
    
    // Handle other objects by converting to string
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    return value;
  };

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={displayValue()}>
          {displayValue()}
        </div>
      </div>
    </div>
  );
};

// URL renderer with clickable link
const urlRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }

  // Ensure URL has protocol
  const formatUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleUrlClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    window.open(formatUrl(value), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div 
          className="cell_label" 
          style={{
            color: '#1890ff',
            cursor: 'pointer',
            textDecoration: 'underline',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={value}
          onClick={handleUrlClick}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

// Address renderer with location icon
const addressRenderer: React.FC<any> = (props) => {
  const { value } = props;
  if (!value) {
    return <span>-</span>;
  }

  // Handle both string addresses and location objects
  const displayAddress = typeof value === 'object' ? value.address || value.location : value;
  
  if (!displayAddress) {
    return <span>-</span>;
  }

  const handleAddressClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    // Open in Google Maps
    const encodedAddress = encodeURIComponent(displayAddress);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div 
          className="cell_label" 
          style={{
            cursor: 'pointer',
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={displayAddress}
          onClick={handleAddressClick}
        >
          📍 {displayAddress}
        </div>
      </div>
    </div>
  );
};
// Lead ID Renderer - Combines sequencePrefix and sequenceId
const leadIdRenderer: React.FC<any> = (props) => {
  const { data } = props;

  if (!data || !data.sequencePrefix || !data.sequenceId) {
    return <span>-</span>;
  }

  const leadId = `${data.sequencePrefix}-${data.sequenceId}`;

  return (
    <div className="h-100 align-items-center d-flex">
      <div className="lead_cell">
        <div className="cell_label" title={leadId}>
          {leadId}
        </div>
      </div>
    </div>
  );
};

export {
  labelRenderer,
  nameRenderer,
  phoneRenderer,
  sourceRenderer,
  statusRenderer,
  lastActivityRenderer,
  assignedToRenderer,
  locationRenderer,
  dateRenderer,
  amountRenderer,
  phoneRendererTest,
  dateTimeRenderer,
  saleValueRenderer,
  campaignNameRenderer,
  formNameRenderer,
  createdByRenderer,
  editableCustomFieldRenderer,
  urlRenderer,
  addressRenderer,
  leadIdRenderer,
};
